/**
 * RequestsHubWithLeaveApprovals — composition-root wiring for the 11F.6
 * Requests hub.
 *
 * `features/requests` (11E.5 reschedule/cancel/unenroll queue) and
 * `features/attendance` (11E.6 leave queue) may not import each other, so
 * the render-prop wiring lives here — same pattern as
 * `sessionRegister.tsx`/`bookingPickers.tsx`.
 *
 * The Leave approvals tab is gated to owner/admin/manager, mirroring the
 * backend's `_ADMIN_ROLES` on `GET /v1/leaves` exactly (backend/app/
 * features/leaves/router.py) — a 'staff' caller (teacher or
 * staff_type=office_manager) gets a hub with only the reschedule tab, not
 * a disabled/empty leave tab, since the endpoint would 403 them either way.
 */

import React from 'react';
import { useAuth } from '@/context';
import { RequestsHubPage } from '@/features/requests';
import { LeaveApprovalQueuePage, useLeaveQueue } from '@features/attendance';

const LEAVE_APPROVAL_ROLES = new Set(['owner', 'admin', 'manager']);

export const RequestsHubWithLeaveApprovals: React.FC = () => {
  const { user } = useAuth();
  const canApproveLeaves = Boolean(user?.role && LEAVE_APPROVAL_ROLES.has(user.role));

  const { data: leaveQueue } = useLeaveQueue('pending', canApproveLeaves);

  return (
    <RequestsHubPage
      renderLeaveApprovalsTab={canApproveLeaves ? () => <LeaveApprovalQueuePage /> : undefined}
      leavePendingCount={leaveQueue?.total ?? 0}
    />
  );
};
